import { TipoPeriodoDescanso } from '@/modules/business/periodo/enums/tipo-periodo-descanso.enum';

// Interface para processo na lista
export interface ProcessoItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
  /** Usado na etapa Recursos. */
  tipoRecurso?: string;
}

// Interface para autor e natureza da ação
export interface AutorNaturezaItem {
  id: string;
  autor: string;
  naturezaAcao: string;
  dataAdicao: Date;
}

// Interface para audiência presencial
export interface AudienciaPresencialItem {
  id: string;
  audienciaPresencial: string;
  numeroProtocolo: string;
  data: Date;
}

// Interface para audiência remota
export interface AudienciaRemotaItem {
  id: string;
  audienciaRemota: string;
  numeroProcesso: string;
  data: Date;
}

// Interface para atendimento presencial
export interface AtendimentoPresencialItem {
  id: string;
  arquivo: File | null;
  arquivoNome?: string;
  arquivoBase64?: string; // Base64 do arquivo (para salvar no banco)
  observacao: string;
}

// Interface para atendimento remoto
export interface AtendimentoRemotoItem {
  id: string;
  arquivo: File | null;
  arquivoNome?: string;
  arquivoBase64?: string; // Base64 do arquivo (para salvar no banco)
  observacao: string;
}

// Interface para sentença/acórdão com fixação de honorários
export interface HonorarioSentencaItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para cumprimento de sentença de honorários
export interface HonorarioCumprimentoItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para ofício requisitório
export interface OficioRequisitorioItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para demais ofícios
export interface OficioDemaisItem {
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}

// Interface para designação especial (processos)
export interface DesignacaoEspecialProcessoItem {
  id: string;
  tipoPeca: string;
  numeroProcesso: string;
  defensoriaId: number | null;
  defensoriaLabel?: string;
  dataAdicao: Date;
}

// Interface para designação especial (audiências)
export interface DesignacaoEspecialAudienciaItem {
  id: string;
  audiencia: string;
  numeroProcesso: string;
  data: Date;
  defensoriaId: number | null;
  defensoriaLabel?: string;
  dataAdicao: Date;
}

// Interface para sustentação oral
export interface SustentacaoOralItem {
  id: string;
  numeroProcesso: string;
  parte: string;
  dataAdicao: Date;
}

// Interface para acordos extrajudiciais
export interface AcordoExtrajudicialItem {
  id: string;
  nomeParte: string;
  anexo: File | null;
  anexoNome?: string;
  anexoBase64?: string; // Base64 do arquivo (para salvar no banco)
  dataAdicao: Date;
}

// Interface para opções de natureza da ação
export interface NaturezaAcaoOption {
  value: string;
  label: string;
  /** Indica se o tipo de movimentação é de núcleo (vindo da API). */
  nucleo?: boolean;
  relatorioCivel?: boolean;
  relatorioCriminal?: boolean;
}

// Interface para status de comparecimento por dia
export interface DiaComparecimentoStatus {
  dia: number;
  mes?: string; // Mês do status (opcional, padrão é o mês atual)
  ano?: number; // Ano do status (opcional, padrão é o ano atual)
  status: 'presente' | 'ausente' | 'remoto' | 'impedimento' | 'nao_marcado';
  /** Preenchido pela corregedoria ao dar aval: true = remoto validado (conta como presença) */
  remotoValidado?: boolean;
  /** Corregedoria aceitou a justificativa de ausência para este dia */
  ausenteAceitoCorregedoria?: boolean;
  justificativaTexto?: string;
  justificativaArquivo?: File | null;
  justificativaArquivoNome?: string;
  justificativaArquivoBase64?: string; // Base64 do arquivo (para salvar no banco)
  tipoImpedimento?: TipoPeriodoDescanso;
  impedimentoDescricao?: string;
  /** Preenchido a partir de relatório do mês anterior/posterior (somente leitura; não persiste no save). */
  relatorioOrigemId?: number;
  relatorioOrigemPeriodo?: string;
}

// Interface para representar um dia no calendário com suas informações de mês/ano
export interface DiaCalendario {
  dia: number;
  mes: string;
  ano: number;
  isMesAtual: boolean; // Indica se o dia pertence ao mês atual sendo visualizado
}

export type WizardStep = { id: number; title: string; shortTitle: string };

// Definição das etapas do wizard
export const WIZARD_STEPS: WizardStep[] = [
  { id: 1, title: 'Comparecimento ao Fórum', shortTitle: 'Comparecimento' },
  { id: 2, title: 'Autor e Natureza da Ação', shortTitle: 'Autor/Natureza' },
  { id: 3, title: 'Contestação', shortTitle: 'Contestação' },
  { id: 4, title: 'Reconvenção', shortTitle: 'Reconvenção' },
  { id: 5, title: 'Réplica', shortTitle: 'Réplica' },
  { id: 6, title: 'Manifestação nos Autos', shortTitle: 'Manifestação' },
  { id: 7, title: 'Razões Finais', shortTitle: 'Razões Finais' },
  { id: 8, title: 'Audiências/Sessões', shortTitle: 'Aud./Sessões' },
  { id: 9, title: 'Sustentação Oral', shortTitle: 'Sust. Oral' },
  { id: 10, title: 'Recursos', shortTitle: 'Recursos' },
  { id: 11, title: 'Atendimentos', shortTitle: 'Atendimentos' },
  { id: 12, title: 'Acordos Extrajudiciais', shortTitle: 'Acordos' },
  { id: 13, title: 'Honorários', shortTitle: 'Honorários' },
  { id: 14, title: 'Ofícios Expedidos', shortTitle: 'Ofícios' },
  { id: 15, title: 'Designação Especial (Processos)', shortTitle: 'Desig. Processos' },
  { id: 16, title: 'Designação Especial (Audiências)', shortTitle: 'Desig. Audiências' },
  { id: 17, title: 'Outros Procedimentos', shortTitle: 'Outros' },
];

// Lista de tipos de audiências (presenciais e remotas)
export const TIPOS_AUDIENCIAS: string[] = [
  'Audiência de Instrução',
  'Audiência de Conciliação',
  'Audiência de Justificação',
  'Audiência de Oitiva',
  'Audiência de Interrogatório',
  'Audiência de Pronúncia',
  'Audiência de Sentença',
  'Audiência de Instrução e Julgamento',
  'Audiência de Conciliação e Instrução',
  'Audiência Virtual',
  'Audiência Remota',
  'Audiência por Videoconferência',
  'Outra',
];

// Função para obter o nome do tipo de período
export const getTipoPeriodoNome = (tipo: TipoPeriodoDescanso): string => {
  const nomes: Record<TipoPeriodoDescanso, string> = {
    [TipoPeriodoDescanso.Ferias]: 'Férias',
    [TipoPeriodoDescanso.FolgaNatalicia]: 'Folga Natalícia',
    [TipoPeriodoDescanso.AbonoTrabalhado]: 'Abono Trabalhado',
    [TipoPeriodoDescanso.AbonoEspecial]: 'Abono Especial',
    [TipoPeriodoDescanso.LicencaPremio]: 'Licença Prêmio',
    [TipoPeriodoDescanso.LicencaOutros]: 'Licença Outros',
    [TipoPeriodoDescanso.Afastamento]: 'Afastamento',
  };
  return nomes[tipo] || 'Impedimento';
};

// Interface para os dados do formulário de relatório cíve
export interface RelatorioCivelFormData {
  // Informações básicas
  defensoriaId: number;
  defensoriaName: string;
  mes: string;
  ano: number;

  // Etapa 1 - Comparecimento ao Fórum
  comparecimento: {
    diasComparecimento: number;
    horasTrabalho: number;
    audienciasRealizadas: number;
    audienciasConciliacao: number;
    audienciasInstrucao: number;
    despachos: number;
    diasStatus: DiaComparecimentoStatus[]; // Array com status de cada dia do mês
  };

  // Etapa 2 - Autor e Natureza da Ação
  autorNatureza: {
    itens: AutorNaturezaItem[];
  };

  // Etapa 3 - Contestação (lista de processos)
  contestacao: {
    processos: ProcessoItem[];
  };

  // Etapa 4 - Reconvenção (lista de processos)
  reconvencao: {
    processos: ProcessoItem[];
  };

  // Etapa 5 - Réplica (lista de processos)
  replica: {
    processos: ProcessoItem[];
  };

  // Etapa 6 - Manifestação nos Autos (lista de processos)
  manifestacao: {
    processos: ProcessoItem[];
  };

  // Etapa 7 - Razões Finais (lista de processos)
  razoesFinais: {
    processos: ProcessoItem[];
  };

  // Etapa 7 - Audiências (presenciais e remotas)
  audiencias: {
    presenciais: AudienciaPresencialItem[];
    remotas: AudienciaRemotaItem[];
  };

  // Etapa 8 - Recursos (lista de processos)
  recursos: {
    processos: ProcessoItem[];
  };

  // Etapa 9 - Atendimento (presenciais e remotos)
  atendimento: {
    /** Total informado de atendimentos presenciais (independente dos anexos). */
    totalPresenciais: number;
    /** Total informado de atendimentos remotos (independente dos anexos). */
    totalRemotas: number;
    presenciais: AtendimentoPresencialItem[];
    remotas: AtendimentoRemotoItem[];
  };

  // Etapa 10 - Acordos Extrajudiciais
  acordosExtrajudiciais: {
    itens: AcordoExtrajudicialItem[];
  };

  // Etapa 11 - Honorários (sentença/acórdão e cumprimento)
  honorarios: {
    sentencaAcordao: HonorarioSentencaItem[];
    cumprimento: HonorarioCumprimentoItem[];
  };

  // Etapa 12 - Ofícios Expedidos (requisitórios e demais)
  oficiosExpedidos: {
    requisitorios: OficioRequisitorioItem[];
    demais: OficioDemaisItem[];
  };

  // Etapa 13 - Designação Especial (Processos)
  designacaoEspecialProcessos: {
    itens: DesignacaoEspecialProcessoItem[];
  };

  // Etapa 14 - Designação Especial (Audiências)
  designacaoEspecialAudiencias: {
    itens: DesignacaoEspecialAudienciaItem[];
  };

  // Etapa 16 - Sustentação Oral
  sustentacaoOral: {
    itens: SustentacaoOralItem[];
  };

  // Etapa 17 - Outros Procedimentos (lista de processos)
  outrosProcedimentos: {
    processos: ProcessoItem[];
  };

  // Relatório criminal — Outras Manifestações (lista de processos)
  outrasManifestacoes: {
    processos: ProcessoItem[];
  };

  // Relatório criminal — Ciência (lista de processos)
  ciencia: {
    processos: ProcessoItem[];
  };
}

